import { useCallback, useEffect, useState, useTransition } from "react";
import { useNavigate } from "react-router-dom";
import { useQuote } from "./useQuote";
import { useOffer } from "./useOffer";
import { getClubData } from "services/cdsService";
import { getMLTAOnlineQuote } from "services/onlineApplicationQuoteService";
import { getLoyaltyTravelAccidentPlanCode, type ClubCodes } from "domain/planCodes";
import { createDate, toLocalISODateString } from "utils/age";
import { transformApiBenefitToMLTABenefit } from "utils/mltaQuoteUtils";
import { toUsStates } from "constants/validations";
import { getQuote, storeQuote } from "services/onlineApplicationService";
import { toLowerCaseGenderFromSingleLetter } from "utils/conversions";
import { pushGTMEvent } from "utils/gtmEvent";
import { GA_EVENT_CONFIGS } from "constants/gaEvents";
import type { MLTAApplicationFormReturn } from "types/Card";
import { ROUTES } from "routes/constants";
import { useProductContext } from "context/ProductContext";
import type { PlanTier } from "types/Plan";

interface UseMLTAQuoteFormProps {
    form: MLTAApplicationFormReturn;
    quoteFormData: {
        dateOfBirth?: { month?: string; day?: string; year?: string };
        state?: string;
        zipCode?: string;
    };
}

interface UseMLTAQuoteFormReturn {
    // Validation state
    isUnavailableState: boolean;

    // Loading state
    isLoadingQuotes: boolean;
    quoteError: string | null;

    // Form field values (for controlled components)
    dobMonth?: string;
    dobDay?: string;
    dobYear?: string;
    stateValue?: string;
    zipCodeValue?: string;

    // Display values
    firstName?: string;

    // Event handlers
    handleSubmit: () => Promise<void>;
}

const MLTA_UNAVAILABLE_STATES = ["MA", "NM", "NY", "VT", "OR", "WA"];

/**
 * Validates all MLTA quote form fields
 * Similar to useDTOQuoteForm's validateQuoteFields approach
 */
const validateMLTAQuoteFields = async (form: MLTAApplicationFormReturn): Promise<boolean> => {
    const results = await Promise.all([
        form.trigger("quote.dateOfBirth.month"),
        form.trigger("quote.dateOfBirth.day"),
        form.trigger("quote.dateOfBirth.year"),
        form.trigger("quote.state"),
        form.trigger("quote.zipCode"),
    ]);

    return results.every((result) => result);
};

/**
 * Restores date of birth fields from saved quote data
 */
const restoreDateOfBirth = (quoteData: any, setValue: any): void => {
    if (quoteData.dobMonth) setValue("quote.dateOfBirth.month", quoteData.dobMonth);
    if (quoteData.dobDay) setValue("quote.dateOfBirth.day", quoteData.dobDay);
    if (quoteData.dobYear) setValue("quote.dateOfBirth.year", quoteData.dobYear);
};

const transformSavedQuoteToMLTAQuoteData = (saved: {
    dateOfBirth?: number[];
    state?: string;
    gender?: string;
    coveragePlan?: string;
    includeFamilyCoverage?: boolean;
}) => {
    const [year, month, day] = saved.dateOfBirth ?? [0, 0, 0];
    return {
        state: saved.state ?? "",
        dobMonth: month > 0 ? month.toString().padStart(2, "0") : "",
        dobDay: day > 0 ? day.toString() : "",
        dobYear: year > 0 ? year.toString() : "",
        applicantType: "member" as const,
        selectedTier: (saved.coveragePlan as PlanTier) ?? undefined,
        selectedCoverageType:
            saved.includeFamilyCoverage === true
                ? ("Family" as const)
                : saved.includeFamilyCoverage === false
                ? ("Individual" as const)
                : undefined,
        gender: saved.gender ? (toLowerCaseGenderFromSingleLetter(saved.gender) as "male" | "female") : undefined,
    };
};

/**
 * Custom hook to manage MLTA (Member Loyalty Travel Accident) quote form business logic
 * Handles state management, validation, API calls, and Redux persistence for MLTA
 */
export const useMLTAQuoteForm = ({ form, quoteFormData }: UseMLTAQuoteFormProps): UseMLTAQuoteFormReturn => {
    const navigate = useNavigate();
    const { saveQuote, quoteData } = useQuote();
    const { offerData } = useOffer();
    const { isMLTA } = useProductContext();
    const { setValue } = form;

    // ========== State ==========
    const [isLoadingQuotes, startQuoteTransition] = useTransition();
    const [quoteError, setQuoteError] = useState<string | null>(null);

    // ========== Computed Values ==========
    const dobMonth = quoteFormData?.dateOfBirth?.month;
    const dobDay = quoteFormData?.dateOfBirth?.day;
    const dobYear = quoteFormData?.dateOfBirth?.year;
    const stateValue = quoteFormData?.state;
    const zipCodeValue = quoteFormData?.zipCode;

    const isUnavailableState = stateValue ? MLTA_UNAVAILABLE_STATES.includes(stateValue) : false;

    // ========== Effects ==========
    useEffect(() => {
        if (!isMLTA || !offerData?.invitationCode || quoteData) {
            return;
        }
        let cancelled = false;
        getQuote(offerData.invitationCode)
            .then((saved) => {
                if (cancelled || Object.keys(saved).length === 0) return;
                saveQuote(transformSavedQuoteToMLTAQuoteData(saved));
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [isMLTA, offerData?.invitationCode, quoteData, saveQuote]);

    useEffect(() => {
        if (!isMLTA) return;

        if (quoteData) {
            restoreDateOfBirth(quoteData, setValue);
        }

        // Prefill with Data from Offer / Campaign table
        if (offerData?.state) setValue("quote.state", offerData.state);
        if (offerData?.zipCode) setValue("quote.zipCode", offerData.zipCode);
        // If user is coming back and already has data in the quote, use that instead
        if (quoteData?.state) setValue("quote.state", quoteData.state);
        if (quoteData?.zipCode) setValue("quote.zipCode", quoteData.zipCode);
    }, [isMLTA, quoteData, offerData, setValue]);

    // ========== Event Handlers ==========
    const handleSubmit = useCallback(async () => {
        if (isUnavailableState) {
            pushGTMEvent(GA_EVENT_CONFIGS.QUOTE_STATE_NOT_AVAILABLE);
            return;
        }

        const isValid = await validateMLTAQuoteFields(form);
        if (!isValid) {
            return;
        }

        setQuoteError(null);

        startQuoteTransition(async () => {
            try {
                // 1. Get club data from CDS service using zipCode
                const clubData = await getClubData(zipCodeValue!);

                // 2. Get plan code based on club code (MLTA or ACLTA)
                const planCode = getLoyaltyTravelAccidentPlanCode(clubData.clubCode as ClubCodes);
                // 3. Build date of birth string in ISO format
                const dateOfBirth = toLocalISODateString(
                    createDate(parseInt(dobYear!, 10), parseInt(dobMonth!, 10), parseInt(dobDay!, 10))
                );

                // 4. Get state abbreviation
                const usState = toUsStates(stateValue!);
                if (!usState) {
                    throw new Error("Invalid state selected");
                }

                // 5. Call MLTA quote API
                const quoteResponse = await getMLTAOnlineQuote({
                    state: usState,
                    dateOfBirth,
                    product: planCode,
                    membershipLength: offerData?.membershipLength ?? 0,
                    keyCode: offerData?.keyCode,
                });

                // 6. Process and validate benefits
                const rawBenefits = quoteResponse.benefits ?? [];
                const benefits = rawBenefits
                    .map((benefit) => transformApiBenefitToMLTABenefit(benefit))
                    .filter((b): b is NonNullable<typeof b> => b !== null);

                if (benefits.length < 1) {
                    pushGTMEvent(GA_EVENT_CONFIGS.QUOTE_INVALID);
                    throw new Error("Quote result was unexpectedly missing benefits.");
                }

                saveQuote({
                    state: stateValue!,
                    dobMonth: dobMonth!,
                    dobDay: dobDay!,
                    dobYear: dobYear!,
                    applicantType: "member",
                    zipCode: zipCodeValue,
                    clubCode: clubData.clubCode,
                    planCode: quoteData?.planCode ?? planCode,
                    keyCode: benefits[0].correctKeycode,
                    membershipLevel: benefits[0].level,
                    mltaBenefits: benefits,
                    selectedTier: quoteData?.selectedTier,
                    selectedCoverageType: quoteData?.selectedCoverageType,
                });

                // 7b. Persist quote to backend (fire-and-forget)
                if (offerData?.invitationCode) {
                    storeQuote({
                        userRef: offerData.invitationCode,
                        dateOfBirth,
                        state: usState,
                    });
                }

                pushGTMEvent(GA_EVENT_CONFIGS.QUOTE_SAVE);

                // 8. Navigate to quote results page
                navigate(ROUTES.QUOTE_RESULTS);
                setTimeout(() => {
                    document.getElementById("hero-card")?.scrollIntoView({ behavior: "auto" });
                }, 0);
            } catch (error) {
                console.error("Error fetching MLTA quotes:", error);
                setQuoteError(error instanceof Error ? error.message : "Failed to fetch quotes");
                pushGTMEvent(GA_EVENT_CONFIGS.QUOTE_FETCH_ERROR);
                navigate(ROUTES.SOMETHING_WENT_WRONG);
            }
        });
    }, [
        isUnavailableState,
        form,
        startQuoteTransition,
        saveQuote,
        stateValue,
        dobMonth,
        dobDay,
        dobYear,
        zipCodeValue,
        offerData?.invitationCode,
        offerData?.membershipLength,
        offerData?.keyCode,
        navigate,
        quoteData?.planCode,
        quoteData?.selectedTier,
        quoteData?.selectedCoverageType,
    ]);

    return {
        isUnavailableState,
        isLoadingQuotes,
        quoteError,
        dobMonth,
        dobDay,
        dobYear,
        stateValue,
        zipCodeValue,
        firstName: offerData?.firstName,
        handleSubmit,
    };
};
